import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Alert,
  Snackbar,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import Footer from '../components/Footer';

function Clients({ user }) {
  const [clients, setClients] = useState([]);
  const [maisonOptions, setMaisonOptions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, client: null });
  const [repairingMaisons, setRepairingMaisons] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'Belgique',
    numero_tva: '',
    maison_repos: '',
    peppol_endpoint_scheme: '',
    peppol_endpoint_id: '',
  });

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get('/api/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
      showSnackbar('Erreur lors du chargement des clients', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/maisons-repos', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const arr = Array.isArray(data) ? data : [];
          // Exclusion volontaire de "Notre foyer" des menus déroulants de sélection
          const filtered = arr.filter(
            (m) => String(m?.nom || '').trim().toLowerCase() !== 'notre foyer'
          );
          setMaisonOptions(filtered);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData(client);
    } else {
      setEditingClient(null);
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        code_postal: '',
        ville: '',
        pays: 'Belgique',
        numero_tva: '',
        maison_repos: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingClient) {
        // Modifier un client existant
        await axios.put(`/api/clients/${editingClient.id}`, formData);
        showSnackbar('Client modifié avec succès', 'success');
        fetchClients();
      } else {
        // Créer un nouveau client
        await axios.post('/api/clients', formData);
        showSnackbar('Client créé avec succès', 'success');
        fetchClients();
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (error.response?.data?.error) {
        showSnackbar(error.response.data.error, 'error');
      } else {
        showSnackbar('Erreur lors de la sauvegarde', 'error');
      }
    }
  };

  const handleDeleteClick = (client) => {
    setDeleteDialog({ open: true, client });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/clients/${deleteDialog.client.id}`);
      showSnackbar('Client supprimé avec succès', 'success');
      fetchClients();
      setDeleteDialog({ open: false, client: null });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      if (error.response?.data?.error) {
        showSnackbar(error.response.data.error, 'error');
      } else {
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, client: null });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRepairMaisonDepuisFactures = async () => {
    if (!window.confirm(
      'Remplir la maison de repos sur les fiches vides, en utilisant la maison indiquée sur la dernière facture (hors facture PEPPOL consolidée) ?'
    )) {
      return;
    }
    setRepairingMaisons(true);
    try {
      const { data } = await axios.post('/api/clients/repair-maison-depuis-factures');
      showSnackbar(
        `${data.message || 'OK'} (${data.clients_mis_a_jour || 0} fiche(s) mise(s) à jour).`,
        'success'
      );
      fetchClients();
    } catch (error) {
      console.error(error);
      showSnackbar(error.response?.data?.error || 'Réparation impossible', 'error');
    } finally {
      setRepairingMaisons(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 8 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Gestion des clients</Typography>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {user && (
            <Button
              variant="outlined"
              color="secondary"
              disabled={repairingMaisons}
              onClick={handleRepairMaisonDepuisFactures}
              title="Recopie la maison de repos sur les fiches vides, à partir de la dernière facture (hors facture PEPPOL consolidée)."
            >
              {repairingMaisons ? 'Réparation…' : 'Réparer la maison (depuis les factures)'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau client
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', flexGrow: 1 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Prénom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell>Ville</TableCell>
                <TableCell>Maison de repos</TableCell>
                <TableCell>N° TVA</TableCell>
                <TableCell>PEPPOL</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client) => (
                <TableRow key={client.id} hover>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {client.nom}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      {client.prenom || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{client.email || '-'}</TableCell>
                  <TableCell>{client.telephone || '-'}</TableCell>
                  <TableCell>
                    {client.adresse ? (
                      <Box>
                        <Typography variant="body2">{client.adresse}</Typography>
                        {client.code_postal && client.ville && (
                          <Typography variant="body2" color="textSecondary">
                            {client.code_postal} {client.ville}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{client.ville || '-'}</TableCell>
                  <TableCell>{client.maison_repos || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {client.numero_tva || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                      {client.peppol_endpoint_scheme && client.peppol_endpoint_id
                        ? `${client.peppol_endpoint_scheme}:${client.peppol_endpoint_id}`
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(client)}
                      title="Modifier"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(client)}
                      title="Supprimer"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog pour ajouter/modifier un client */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingClient ? 'Modifier le client' : 'Nouveau client'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="nom"
                  label="Nom *"
                  value={formData.nom}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="prenom"
                  label="Prénom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="telephone"
                  label="Téléphone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="pays"
                  label="Pays"
                  value={formData.pays}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Maison de repos</InputLabel>
                  <Select
                    name="maison_repos"
                    value={formData.maison_repos}
                    onChange={handleInputChange}
                    label="Maison de repos"
                  >
                    <MenuItem value="">Aucune</MenuItem>
                    {maisonOptions.map((m) => (
                      <MenuItem key={m.id} value={m.nom}>
                        {m.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="numero_tva"
                  label="Numéro de TVA"
                  value={formData.numero_tva}
                  onChange={handleInputChange}
                  fullWidth
                  helperText="ex: BE0123456789"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  E-facturation PEPPOL (destinataire)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="peppol_endpoint_scheme"
                  label="Schéma PEPPOL (EndpointID)"
                  value={formData.peppol_endpoint_scheme || ''}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="ex: 0208, 9925"
                  helperText={
                    maisonOptions.some((m) => m.nom === formData.maison_repos)
                      ? 'Optionnel : résidents — facturation PEPPOL via l’établissement (onglet Maisons de repos).'
                      : 'Code liste ISO 6523 — selon pays / access point'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="peppol_endpoint_id"
                  label="Identifiant PEPPOL"
                  value={formData.peppol_endpoint_id || ''}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="ex: numéro TVA ou entreprise"
                  helperText={
                    maisonOptions.some((m) => m.nom === formData.maison_repos)
                      ? 'Laisser vide si le client est un résident facturé collectivement par la maison.'
                      : 'Point de terminaison PEPPOL du client (factures classiques).'
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="adresse"
                  label="Adresse"
                  value={formData.adresse}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="code_postal"
                  label="Code postal"
                  value={formData.code_postal}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="ville"
                  label="Ville"
                  value={formData.ville}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained">
              {editingClient ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer le client "{deleteDialog.client?.prenom ? `${deleteDialog.client.prenom} ${deleteDialog.client.nom}` : deleteDialog.client?.nom}" ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Annuler</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Footer avec mention Woud */}
      <Footer />
    </Box>
  );
}

export default Clients; 